/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useRef, useState} from 'react';
import {importHistory} from '../../../util/requestSender';
import {Loader, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {ProcessType} from '../../../util/interfaces';
import {AppContext} from '../../misc/appContext';
import {Navigate} from 'react-router';
import HistoryImport from './historyImport';
import AdminProcessController, {AdminProcessRef} from '../adminProcess/adminProcessController';
import {INVOICES_ROUTE} from '../../../util/constants';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export default function HistoryImportController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const [importTillDate, setImportTillDate] = useState<IDate | null>(null);
    const [invNumPrefix, setInvNumPrefix] = useState('{jaar}i-');
    const [invNumStart, setInvNumStart] = useState('0001');
    const [sendEmails, setSendEmails] = useState(false);
    const [loading, setLoading] = useState(false);
    const [startPage, setStartPage] = useState(1);
    const processRef = useRef<AdminProcessRef>(null);
    if (!context.userContext.user?.isSuperAdmin()) return <Navigate to={INVOICES_ROUTE} />;

    function onChangeImportDate(value: IDate | null) {
        setImportTillDate(value);
    }

    function onChangeInvNumPrefix(value: string) {
        setInvNumPrefix(value);
    }

    function onChangeInvNumStart(value: string) {
        setInvNumStart(value);
    }

    function onChangeSendEmails(value: boolean) {
        setSendEmails(value);
    }

    function onChangeStartPage(value: number) {
        setStartPage(value);
    }

    function onSubmit() {
        const selectedUser = processRef.current?.getSelectedUser();
        const selectedAccount = processRef.current?.getSelectedAccount();
        if (!selectedUser || !selectedAccount) return;
        setLoading(true);
        importHistory({
            data: {
                account_id: selectedAccount.id,
                user_id: selectedUser.id,
                import_till_date: importTillDate?.format('YYYY-MM-DD') ?? null,
                invoice_num_prefix: invNumPrefix,
                invoice_num_start: invNumStart,
                send_emails: sendEmails,
                start_page: startPage
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    showSnackbar(t('admin.historyImport.startedSnack', 'De import is gestart'));
                    processRef.current?.onLoadProcesses();
                } else showDialog(data.message ?? t('admin.historyImport.genericError', 'Er is iets fout gegaan.'));
            }
        });
    }

    return (
        <AdminProcessController processType={ProcessType.HISTORY_IMPORT} ref={processRef}>
            <Loader active={loading} />
            <HistoryImport
                onChangeImportDate={onChangeImportDate}
                onChangeInvNumPrefix={onChangeInvNumPrefix}
                onChangeInvNumStart={onChangeInvNumStart}
                onChangeSendEmails={onChangeSendEmails}
                onSubmit={onSubmit}
                onChangeStartPage={onChangeStartPage}
                importTillDate={importTillDate}
                invNumPrefix={invNumPrefix}
                invNumStart={invNumStart}
                sendEmails={sendEmails}
                loading={loading}
                startPage={startPage}
            />
        </AdminProcessController>
    );
}
